import { TableColumns, TextLink } from '@teamsnap/snap-ui';
import { APP_URL, TEAMSNAP_URL } from 'core/constants';
import { formatPenniesToDollars } from 'core/utils';
import { DateFormat, DateService } from 'frontend-toolkit';
import * as React from 'react';
import MemberRegistrationStatus from './member-registration-status';
import { MemberRegistration } from './types';

export const getColumns = (orgId: number): TableColumns => ({
  registrationDate: {
    label: 'Date',
    sort: 'date',
    align: 'left',
    width: '120px',
    getBodyProps: (row: MemberRegistration) => ({
      height: 'tall',
      type: 'custom',
      children: DateService.format(
        new Date(row.registrationDate),
        DateFormat.DD_MM_YYYY
      ),
    }),
  },
  registrationFormName: {
    label: 'Registration',
    sort: 'alpha',
    align: 'left',
    width: '400px',
    headerProps: {
      tooltip:
        'Registrations shown here include all forms associated with the selected participant.',
    },
    getBodyProps: (row: MemberRegistration) => ({
      height: 'tall',
      type: 'custom',
      children: (
        <div className="sui-w-[calc(400px-32px)] sui-overflow-hidden sui-whitespace-nowrap sui-flex sui-flex-col sui-gap-y-[4px]">
          <span
            title={row.registrationFormName}
            className="sui-body-dense sui-truncate"
          >
            {row.registrationFormName}
          </span>
          <span
            title={`${row.programName} · ${row.programSeasonName}`}
            data-testid="MemberRegistration-registration-program-season"
            className="sui-caption sui-text-neutral-text-medium sui-truncate"
          >
            {row.programSeasonName}
          </span>
        </div>
      ),
    }),
  },
  participantNames: {
    label: 'Registered For',
    sort: 'alpha',
    align: 'left',
    width: '150px',
    getBodyProps: (row: MemberRegistration) => ({
      height: 'tall',
      type: 'custom',
      children: row.participantNames,
    }),
  },
  registrantName: {
    label: 'Registered By',
    sort: 'alpha',
    align: 'left',
    width: '150px',
    getBodyProps: (row: MemberRegistration) => ({
      height: 'tall',
      type: 'custom',
      children: row.registrantName,
    }),
  },
  orderStatus: {
    label: 'Payment Status',
    sort: 'alpha',
    align: 'left',
    width: '250px',
    headerProps: {
      height: 'tall',
      tooltip: 'Balance and total account for all participants in the order.',
      prepend: 'Order',
    },
    getBodyProps: (row: MemberRegistration) => ({
      height: 'tall',
      type: 'custom',
      children: (
        <MemberRegistrationStatus
          status={row.orderStatus}
          completedPayments={row.completedInstallmentsCount}
          totalPayments={row.installmentPlanPaymentCount}
        />
      ),
    }),
  },
  total: {
    label: 'Total',
    sort: 'numeric',
    align: 'right',
    width: '120px',
    headerProps: {
      prepend: <></>,
    },
    getBodyProps: (row: MemberRegistration) => ({
      height: 'tall',
      type: 'custom',
      children: formatPenniesToDollars(row.total),
    }),
  },
  balance: {
    label: 'Balance',
    sort: 'numeric',
    align: 'right',
    width: '120px',
    headerProps: {
      prepend: <></>,
    },
    getBodyProps: (row: MemberRegistration) => ({
      height: 'tall',
      type: 'custom',
      children: formatPenniesToDollars(row.balance),
    }),
  },
  actions: {
    label: 'Actions',
    align: 'left',
    width: '400px',
    getBodyProps: (row: MemberRegistration) => {
      const {
        divisionId,
        registrationFormId,
        orderId,
        type: registrationType,
      } = row;
      let orderLink;
      let registrationLink;
      if (registrationType === 'reg1') {
        orderLink = `${TEAMSNAP_URL}/divisions/${divisionId}/registration/${registrationFormId}/fees_and_payments_overview?registration_id=${orderId}`;
        registrationLink = `${TEAMSNAP_URL}/divisions/${divisionId}/registration/${registrationFormId}`;
      } else if (registrationType === 'reg2') {
        orderLink = `${APP_URL}/organizations/${orgId}/financials/order/${orderId}/details`;
        registrationLink = `${APP_URL}/organizations/${orgId}/registration/${registrationFormId}/details`;
      }
      return {
        height: 'tall',
        type: 'custom',
        children: (
          <div className="sui-flex sui-gap-3">
            {orderLink && (
              <TextLink
                variantType="primary"
                href={orderLink}
                target="_blank"
                rel="noreferrer"
                className="sui-whitespace-nowrap"
                data-testid="MemberRegistration__view-order-link"
              >
                View order
              </TextLink>
            )}
            {registrationLink && (
              <TextLink
                variantType="primary"
                href={registrationLink}
                target="_blank"
                rel="noreferrer"
                className="sui-whitespace-nowrap"
                data-testid="MemberRegistration__view-registration-link"
              >
                View registration
              </TextLink>
            )}
          </div>
        ),
      };
    },
  },
});
